import { useMemo } from "react";
import { useGetMarketSize } from "../../../../hooks/useGetMarketSize";
import { useCurrentCustomerCounts } from "./useCurrentCustomerCounts";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { OpportunityType } from "../../../../../../legacy/types/companyFilter";

// Helper function to adjust market data based on opportunity type filter
const adjustDataForOpportunityType = (
  originalData: { companyCount: number; totalSpend: number },
  expansionData: { companyCount: number; totalSpend: number },
  currentCustomers: number,
  opportunityType?: OpportunityType
) => {
  switch (opportunityType) {
    case OpportunityType.CUSTOMER:
      // When filtering by customers, set whitespace/expansion/prospect to 0
      return {
        current: currentCustomers,
        whitespace: 0,
        expansion: 0,
        prospect: 0,
      };

    case OpportunityType.WHITESPACE:
      // When filtering by whitespace, set current to 0
      return {
        current: 0,
        whitespace: originalData.companyCount, // All filtered data is whitespace
        expansion: 0,
        prospect: 0,
      };

    case OpportunityType.EXPANSION_WHITESPACE:
      // When filtering by expansion, set current and prospect to 0
      return {
        current: 0,
        whitespace: 0,
        expansion: originalData.companyCount, // All filtered data is expansion
        prospect: 0,
      };

    case OpportunityType.PROSPECT_WHITESPACE:
      // When filtering by prospect, set current and expansion to 0
      return {
        current: 0,
        whitespace: 0,
        expansion: 0,
        prospect: originalData.companyCount, // All filtered data is prospect
      };

    default:
      // No filter applied - show all data with calculated breakdown
      return {
        current: currentCustomers,
        whitespace: originalData.companyCount - currentCustomers,
        expansion: expansionData.companyCount,
        prospect:
          originalData.companyCount -
          expansionData.companyCount -
          currentCustomers,
      };
  }
};

export const useGetMarketSizeBySegment = () => {
  const marketData = useGetMarketSize();
  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const currentOpportunityType = userFilters?.opportunityType?.type;

  const {
    tamCustomers,
    samCustomers,
    somCustomers,
    loading: currentCountLoading,
  } = useCurrentCustomerCounts();

  const tam = useMemo(() => {
    const adjustedData = adjustDataForOpportunityType(
      marketData.tam,
      marketData.tamExpansion,
      tamCustomers,
      currentOpportunityType
    );

    return {
      ...marketData.tam,
      expansion: marketData.tamExpansion,
      currentCustomers: adjustedData.current,
      adjustedWhitespace: adjustedData.whitespace,
      adjustedExpansion: adjustedData.expansion,
      adjustedProspect: adjustedData.prospect,
    };
  }, [
    marketData.tam,
    marketData.tamExpansion,
    tamCustomers,
    currentOpportunityType,
  ]);

  const sam = useMemo(() => {
    const adjustedData = adjustDataForOpportunityType(
      marketData.sam,
      marketData.samExpansion,
      samCustomers,
      currentOpportunityType
    );

    return {
      ...marketData.sam,
      expansion: marketData.samExpansion,
      currentCustomers: adjustedData.current,
      adjustedWhitespace: adjustedData.whitespace,
      adjustedExpansion: adjustedData.expansion,
      adjustedProspect: adjustedData.prospect,
    };
  }, [
    marketData.sam,
    marketData.samExpansion,
    samCustomers,
    currentOpportunityType,
  ]);

  const som = useMemo(() => {
    const adjustedData = adjustDataForOpportunityType(
      marketData.som,
      marketData.somExpansion,
      somCustomers,
      currentOpportunityType
    );

    return {
      ...marketData.som,
      expansion: marketData.somExpansion,
      currentCustomers: adjustedData.current,
      adjustedWhitespace: adjustedData.whitespace,
      adjustedExpansion: adjustedData.expansion,
      adjustedProspect: adjustedData.prospect,
    };
  }, [
    marketData.som,
    marketData.somExpansion,
    somCustomers,
    currentOpportunityType,
  ]);

  return {
    tam,
    sam,
    som,
    isLoading: marketData.isLoading || currentCountLoading,
  };
};
